// The customer screen, shown in place of the style grid: profile (tier,
// mobile, email, since, usual sizes), a summary (lifetime spend, bills,
// average bill, points), every bill in any store and on any till with returns,
// online orders, and the customer's follow-ups. One account across the store
// and the online store. Bills come from Sales History and online orders from
// Omni Connect's orders on the server (found by mobile), so both need a
// connection; the balances are the ones the till loaded.
import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { fashionPosService, salesHistoryService, salesHistoryDetails } from "../../../services";
import { formatMoney } from "../../../utils/fashion/money";
import {
  BILL_LIMIT,
  HISTORY_BILLS,
  accountBalances,
  billStats,
  billsInScope,
  buildBillLinesQuery,
  buildCustomerBillsQuery,
  canScopeToStore,
  customerOnlineOrders,
  mobileKey,
  readBillLines,
  readCustomerBills,
  sizeProfile,
} from "../../../utils/fashion/customerHistory";
import { STATUS_LABEL, buildOnlineOrdersQuery, readOnlineOrders } from "../../../utils/fashion/onlineOrders";
import useCartStore from "../../../stores/useCartStore";
import FashionClientBook from "./FashionClientBook";
import EmptyState from "./EmptyState";
import "./FashionCustomerPanel.css";

const showDate = (d) => (d && dayjs(d).isValid() ? dayjs(d).format("DD MMM YYYY") : d || "");
const initials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .map((w) => w[0] || "")
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";
const itemsLabel = (n) => (n == null ? "" : `${n} ${n === 1 ? "item" : "items"}`);
const TOAST_MS = 3600;
const TOAST_TAG = { ok: "Done", warn: "Check", bad: "Problem", info: "Info" };

// When they became a customer: the record's own date, else their first bill
// when the whole history fitted in one page.
function customerSince(customer, bills) {
  const own = customer?.created || customer?.createdDate || customer?.createdAt;
  if (own && dayjs(own).isValid()) return dayjs(own).format("MMM YYYY");
  if (!bills?.length || bills.length >= BILL_LIMIT) return "";
  const first = bills[bills.length - 1]?.date;
  return first && dayjs(first).isValid() ? dayjs(first).format("MMM YYYY") : "";
}

export default function FashionCustomerPanel({ customer, tillData, isOnline, products, onClose }) {
  const [state, setState] = useState({ status: "loading", bills: [], lines: [] });
  // Mid-bill, the way out goes back to that bill rather than "starting" one.
  const billLines = useCartStore((s) => (s.cart?.items || []).filter((item) => item.weight !== 0).length);
  const online = typeof isOnline === "boolean" ? isOnline : typeof navigator === "undefined" || navigator.onLine;
  const [webOrders, setWebOrders] = useState({ status: "loading", rows: [] });
  const [scope, setScope] = useState("all");
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;
  const mobile = mobileKey(customer?.mobileNo);
  const productById = useMemo(() => {
    const map = new Map((products || []).map((p) => [p.mProductId, p]));
    return (id) => map.get(id);
  }, [products]);

  // One message at a time, in one place.
  const say = (text, tone = "ok") => {
    clearTimeout(toastTimer.current);
    if (!text) {
      setToast(null);
      return;
    }
    setToast({ text, tone });
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  };
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  useEffect(() => {
    if (!online || !customer?.cwrCustomerId) return undefined;
    let live = true;
    setState({ status: "loading", bills: [], lines: [] });
    (async () => {
      try {
        const bills = readCustomerBills(await salesHistoryService(buildCustomerBillsQuery(customer)), customer);
        if (bills === null) throw new Error("no bills");
        const recentSales = bills.filter((b) => !b.isReturn).slice(0, HISTORY_BILLS);
        const details = await Promise.all(recentSales.map((b) => salesHistoryDetails(buildBillLinesQuery(b.id)).catch(() => null)));
        if (live) setState({ status: "done", bills, lines: details.flatMap(readBillLines) });
      } catch {
        if (live) setState({ status: "error", bills: [], lines: [] });
      }
    })();
    return () => {
      live = false;
    };
  }, [customer?.cwrCustomerId, online]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!online || !bunitId || mobile.length < 6) return undefined;
    let live = true;
    setWebOrders({ status: "loading", rows: [] });
    (async () => {
      let rows = null;
      try {
        rows = readOnlineOrders(await fashionPosService(buildOnlineOrdersQuery(bunitId, "ALL", mobile)));
      } catch {
        rows = null;
      }
      if (live) setWebOrders(rows === null ? { status: "error", rows: [] } : { status: "done", rows: customerOnlineOrders(rows, mobile) });
    })();
    return () => {
      live = false;
    };
  }, [online, bunitId, mobile]);

  const sizes = useMemo(() => sizeProfile(state.lines, productById), [state.lines, productById]);
  const { points, credit } = accountBalances(customer);
  const loaded = state.status === "done";
  const stats = billStats(state.bills);
  const scoped = canScopeToStore(state.bills);
  const shownBills = billsInScope(state.bills, scoped ? scope : "all", bunitId);
  const since = customerSince(customer, loaded ? state.bills : null);
  const meta = [customer?.mobileNo, customer?.email, since ? `customer since ${since}` : null].filter(Boolean).join(" · ");
  const tier = customer?.loyaltyLevel?.name;
  const money0 = (v) => formatMoney(Math.round(v), tillData);

  const billBody = !online ? (
    <EmptyState icon="offline" title="You're offline" hint="Bill history needs a connection. Reconnect the till and try again." />
  ) : state.status === "loading" ? (
    <div className="fp-cust-empty" role="status">
      Loading this customer's bills…
    </div>
  ) : state.status === "error" ? (
    <EmptyState icon="error" role="alert" title="Couldn't load this customer's bills. Check the connection and try again." />
  ) : shownBills.length === 0 ? (
    <EmptyState
      title="No bills yet"
      hint={state.bills.length ? "No bills from this store. Choose All stores to see the rest." : "Bills appear here after the first sale to this customer."}
      action={state.bills.length ? { label: "Show all stores", onClick: () => setScope("all") } : undefined}
    />
  ) : (
    <div className="fp-cust-tscroll">
      <table className="fp-cust-tbl">
        <thead>
          <tr>
            <th>Date</th>
            <th>Bill no</th>
            <th>Store</th>
            <th>Items</th>
            <th className="is-r">Amount</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {shownBills.map((b) => (
            <tr key={b.id || b.documentno}>
              <td>{showDate(b.date)}</td>
              <td>
                <b>{b.documentno}</b>
              </td>
              <td>{b.store}</td>
              <td>{itemsLabel(b.items)}</td>
              <td className="is-r">{formatMoney(b.total, tillData)}</td>
              <td>
                <span className={`fp-cust-pill ${b.isReturn ? "is-info" : "is-ok"}`}>{b.isReturn ? "Returned" : "Completed"}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <section className="fp-cust" aria-label={`Customer ${customer?.name || ""}`}>
      <div className="fp-cust-main">
        <section className="fp-cust-card" aria-label="Profile">
          <div className="fp-cust-row fp-cust-profile">
            <span className="fp-cust-av" aria-hidden="true">
              {initials(customer?.name)}
            </span>
            <div className="fp-cust-grow">
              <div className="fp-cust-row">
                <h2 className="fp-cust-name">{customer?.name || "Customer"}</h2>
                {tier && <span className="fp-cust-pill is-info">{tier}</span>}
              </div>
              {meta && <p className="fp-cust-meta">{meta}</p>}
            </div>
            <button type="button" className="fp-cust-btn" onClick={onClose}>
              {billLines > 0 ? "Back to bill" : "Start a bill"}
            </button>
          </div>
          {(sizes.length > 0 || credit !== null) && (
            <ul className="fp-cust-prefs" aria-label="Preferences">
              {sizes.map((s) => (
                <li key={s.size} className="fp-cust-pill">
                  Size {s.size} · {s.qty} bought
                </li>
              ))}
              {credit !== null && <li className="fp-cust-pill is-ok">Store credit {formatMoney(credit, tillData)}</li>}
            </ul>
          )}
        </section>

        <section className="fp-cust-card fp-cust-stats" aria-label="Customer summary">
          <div>
            <span>Lifetime spend</span>
            <b>{loaded ? money0(stats.spend) : "–"}</b>
          </div>
          <div>
            <span>Bills</span>
            <b>{loaded ? stats.bills : "–"}</b>
          </div>
          <div>
            <span>Average bill</span>
            <b>{loaded ? money0(stats.average) : "–"}</b>
          </div>
          <div>
            <span>Points</span>
            <b>{points !== null ? points.toLocaleString("en-IN") : "–"}</b>
          </div>
        </section>

        <section className="fp-cust-card fp-cust-tbox" aria-label="Bill history">
          <div className="fp-cust-row">
            <h2>Bill history</h2>
            {loaded && scoped && (
              <div className="fp-cust-seg fp-cust-head-seg" role="group" aria-label="Which stores">
                {[
                  ["all", "All stores"],
                  ["here", "This store"],
                ].map(([k, label]) => (
                  <button key={k} type="button" className={scope === k ? "is-on" : ""} aria-pressed={scope === k} onClick={() => setScope(k)}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {billBody}
        </section>

        {online && mobile.length >= 6 && (
          <section className="fp-cust-card" aria-label="Online orders">
            <h2>Online orders</h2>
            {webOrders.status === "error" ? (
              <p className="fp-cust-hint" role="alert">
                Couldn't load this customer's online orders.
              </p>
            ) : webOrders.status === "loading" ? (
              <p className="fp-cust-hint">Loading online orders…</p>
            ) : webOrders.rows.length === 0 ? (
              <p className="fp-cust-hint">No online orders from this store for {customer.mobileNo}.</p>
            ) : (
              <ul className="fp-cust-list">
                {webOrders.rows.map((o) => (
                  <li key={o.id} className="fp-cust-row">
                    <span className="fp-cust-grow">
                      <b>{o.externalId || o.documentNo}</b>
                      {o.channel ? ` · ${o.channel}` : ""} <span className={`fp-cust-pill fp-onl-${o.status}`}>{STATUS_LABEL[o.status] || o.status}</span>
                      <span className="fp-cust-hint"> {showDate(o.ordered)}</span>
                    </span>
                    <span>{formatMoney(o.total, tillData)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>

      <aside className="fp-cust-side" aria-label="Follow-ups for this customer">
        {online && customer?.cwrCustomerId && bunitId ? (
          <FashionClientBook key={customer.cwrCustomerId} customer={customer} bunitId={bunitId} products={products} say={say} />
        ) : (
          <>
            <h2>Follow-ups</h2>
            <p className="fp-cust-hint">Follow-ups need a connection. Reconnect the till to see or add them.</p>
          </>
        )}
      </aside>

      {toast && (
        <div className="fp-cust-toast" role={toast.tone === "bad" || toast.tone === "warn" ? "alert" : "status"}>
          <span className={`fp-cust-pill is-${toast.tone}`}>{TOAST_TAG[toast.tone] || "Info"}</span>
          <span>{toast.text}</span>
        </div>
      )}
    </section>
  );
}
